import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Upload } from 'lucide-react'
import {
  fetchFirm, fetchJurisdictions, fetchMyFirm, updateFirmCantons, updateFirmCantonsFor, updateFirmLetterhead, updateLetterhead,
  uploadFirmLogo, uploadLogoForFirm,
  Letterhead, MyFirm,
} from '../../../lib/api/api'
import { CANTON_CODES } from '../../../shared/cantons'

type FieldKey = 'company_name' | 'address' | 'postal_code_city' | 'phone' | 'email' | 'website' | 'footer'

// LETTERHEAD_KEYS minus logo_path (server: src/database/models/firms.py); the
// logo itself is a file, uploaded through uploadFirmLogo, not this form.
const FIELD_KEYS: FieldKey[] = ['company_name', 'address', 'postal_code_city', 'phone', 'email', 'website', 'footer']

type FormState = Record<FieldKey, string>
const emptyForm: FormState = { company_name: '', address: '', postal_code_city: '', phone: '', email: '', website: '', footer: '' }

function errorDetail(error: any, fallback: string): string {
  const detail = error?.response?.data?.detail
  return typeof detail === 'string' && detail ? detail : fallback
}

// Without firmId: the caller's own firm (a firm admin, /firms/mine). With firmId:
// that firm, edited by the super admin (FirmsAdmin), through /firms/{id}.
export default function FirmTab({ firmId }: { firmId?: string } = {}) {
  const { t } = useTranslation()
  const [firm, setFirm] = useState<MyFirm | null>(null)
  const [form, setForm] = useState<FormState>(emptyForm)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [isUploadingLogo, setIsUploadingLogo] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [cantons, setCantons] = useState<string[]>(['TI'])
  const [isSavingCantons, setIsSavingCantons] = useState(false)
  // cantons in the corpus (CANTONAL_LAWS); while only Ticino is, the section is hidden
  const [availableCantons, setAvailableCantons] = useState<string[]>(['TI'])

  useEffect(() => {
    let active = true
    fetchJurisdictions()
      .then((res) => {
        if (active) setAvailableCantons(res.data.cantons.filter((c) => c.available).map((c) => c.code))
      })
      .catch(() => { /* offline or older server: Ticino only */ })
    return () => { active = false }
  }, [])
  const offersCantons = availableCantons.some((code) => code !== 'TI')

  useEffect(() => {
    let active = true
    ;(firmId ? fetchFirm(firmId) : fetchMyFirm())
      .then((res) => {
        if (!active) return
        setFirm(res.data)
        const next = { ...emptyForm }
        FIELD_KEYS.forEach((key) => { next[key] = res.data.letterhead[key] || '' })
        setForm(next)
        setCantons(res.data.cantons?.length ? res.data.cantons : ['TI'])
      })
      .catch(() => { if (active) setError(t('firm.errors.loadFailed')) })
      .finally(() => { if (active) setIsLoading(false) })
    return () => { active = false }
  }, [firmId, t])

  const handleFieldChange = (key: FieldKey) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }))

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSaving(true)
    setError(null)
    setSuccess(null)
    try {
      const body: Omit<Letterhead, 'logo_path'> = { ...form }
      const res = firmId ? await updateFirmLetterhead(firmId, body) : await updateLetterhead(body)
      setFirm(res.data)
      setSuccess(t('firm.letterhead.saved'))
    } catch (err) {
      setError(errorDetail(err, t('firm.errors.saveFailed')))
    } finally {
      setIsSaving(false)
    }
  }

  const toggleCanton = (code: string) =>
    setCantons((current) => current.includes(code) ? current.filter((c) => c !== code) : [...current, code])

  const handleSaveCantons = async () => {
    setIsSavingCantons(true)
    setError(null)
    setSuccess(null)
    try {
      // in registry order, whatever the order of the clicks
      const ordered = CANTON_CODES.filter((code) => cantons.includes(code))
      const res = firmId ? await updateFirmCantonsFor(firmId, ordered) : await updateFirmCantons(ordered)
      setFirm(res.data)
      setCantons(res.data.cantons?.length ? res.data.cantons : ['TI'])
      setSuccess(t('firm.cantons.saved'))
    } catch (err) {
      setError(errorDetail(err, t('firm.errors.saveFailed')))
    } finally {
      setIsSavingCantons(false)
    }
  }

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setIsUploadingLogo(true)
    setError(null)
    setSuccess(null)
    try {
      const res = firmId ? await uploadLogoForFirm(firmId, file) : await uploadFirmLogo(file)
      setFirm(res.data)
      setSuccess(t('firm.logo.saved'))
    } catch (err) {
      setError(errorDetail(err, t('firm.errors.logoFailed')))
    } finally {
      setIsUploadingLogo(false)
      e.target.value = ''
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="animate-spin text-gray-400" />
      </div>
    )
  }

  return (
    <div className="max-w-2xl space-y-8">
      <div>
        <h2 className="text-xl font-semibold">{t('firm.title')}</h2>
        {firm && <p className="text-sm text-gray-500">{firm.name} — {firm.code}</p>}
      </div>

      {error && <p className="text-red-600 text-sm" role="alert">{error}</p>}
      {success && <p className="text-green-700 text-sm" role="status">{success}</p>}

      <form onSubmit={handleSave} className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-700">{t('firm.letterhead.title')}</h3>
        {FIELD_KEYS.map((key) => (
          <label key={key} className="block text-sm">
            <span className="text-gray-600">{t(`firm.letterhead.fields.${key}`)}</span>
            <input
              type="text"
              value={form[key]}
              onChange={handleFieldChange(key)}
              maxLength={300}
              className="mt-1 w-full border rounded-md px-3 py-1.5"
            />
          </label>
        ))}
        <button
          type="submit"
          disabled={isSaving}
          className="px-3 py-1.5 rounded-md bg-primary-600 text-white text-sm disabled:opacity-50"
        >
          {isSaving && <Loader2 className="inline w-4 h-4 mr-1 animate-spin" />}
          {t('common.save')}
        </button>
      </form>

      {offersCantons && (
        <div className="space-y-2 border-t border-gray-200 pt-6">
          <h3 className="text-sm font-semibold text-gray-700">{t('firm.cantons.title')}</h3>
          <p className="text-sm text-gray-500">{t('firm.cantons.help')}</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-1">
            {CANTON_CODES.map((code) => (
              <label key={code} className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={cantons.includes(code)} onChange={() => toggleCanton(code)}
                  className="w-4 h-4 accent-primary-600" />
                <span>
                  {availableCantons.includes(code)
                    ? t(`cantons.${code}`)
                    : t('chat.context.cantons.upcoming', { name: t(`cantons.${code}`) })}
                </span>
              </label>
            ))}
          </div>
          <button type="button" onClick={handleSaveCantons} disabled={isSavingCantons}
            className="px-3 py-1.5 rounded-md bg-primary-600 text-white text-sm disabled:opacity-50">
            {isSavingCantons && <Loader2 className="inline w-4 h-4 mr-1 animate-spin" />}
            {t('firm.cantons.save')}
          </button>
        </div>
      )}

      <div className="space-y-2 border-t border-gray-200 pt-6">
        <h3 className="text-sm font-semibold text-gray-700">{t('firm.logo.title')}</h3>
        <p className="text-sm text-gray-500">
          {firm?.letterhead.logo_path ? t('firm.logo.present') : t('firm.logo.absent')}
        </p>
        <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border text-sm cursor-pointer">
          {isUploadingLogo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload size={16} />}
          {t('firm.logo.upload')}
          <input
            type="file"
            accept="image/png,image/jpeg"
            onChange={handleLogoChange}
            disabled={isUploadingLogo}
            className="hidden"
          />
        </label>
      </div>
    </div>
  )
}
